// Unit details data from API-3847 (about, land, terrain from GetUnitDetails; climate from
// GetUnitClimate), shaped for display. The readers accept hunt_data's own keys (land_info's
// public_land, geo_weather's weather_data series) or their camelCased API forms, so a unit with
// no data, or a field the API leaves out, comes back null and its section stays hidden.

const pick = (obj, ...keys) => {
  if (!obj) return undefined
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key]
  }
  return undefined
}

const camel = (key) => key.replace(/_([a-z])/g, (_, c) => c.toUpperCase())

// species_info's source: a URL string, or { source_url, source_description }, or a list of them.
// As [{ name, url }] with a name to show.
export const aboutSources = (source) => {
  const list = Array.isArray(source) ? source : source ? [source] : []
  return list.map((s) => {
    if (typeof s === 'string') return /^https?:\/\//.test(s) ? { name: 'Source', url: s } : { name: s }
    const url = pick(s, 'url', 'source_url', 'sourceUrl')
    const name = pick(s, 'description', 'name', 'source_description', 'sourceDescription') || (url ? 'Source' : null)
    return name ? { name, url } : null
  }).filter(Boolean)
}

// About this unit: the species text and the unit notes; null when there's nothing to show.
export const unitAbout = (unitDetails) => {
  const about = unitDetails?.about || {}
  const out = {
    overview: pick(about, 'overview'),
    description: pick(about, 'detailedDescription', 'detailed_description') || unitDetails?.description,
    sources: aboutSources(pick(about, 'source', 'sources')),
    boundary: pick(about, 'boundaryDescription', 'boundary_description'),
    terrainNotes: pick(about, 'terrainNotes', 'terrain_notes'),
    accessNotes: pick(about, 'accessNotes', 'access_notes'),
    weatherNotes: pick(about, 'weatherNotes', 'weather_notes'),
  }
  return Object.entries(out).some(([k, v]) => (k === 'sources' ? v.length > 0 : Boolean(v))) ? out : null
}

// Ownership classes in the order the bar draws them (public first): API-3847's key, then
// land_info's. Wilderness isn't one: it overlaps the agency it lies in (see wildernessShare).
export const LAND_CLASSES = [
  ['usfs', 'usfs_land', 'USFS', '#4f7a28'],
  ['blm', 'blm_land', 'BLM', '#c9a227'],
  ['state', 'state_land', 'State', '#3b82c4'],
  ['nationalPark', 'np_land', 'National Park', '#7a5c2e'],
  ['otherFederal', 'other_federal_land', 'Other Federal', '#8a6fb0'],
  ['military', 'military_land', 'Military', '#9b4d4d'],
  ['tribal', 'indian_land', 'Tribal', '#c2703d'],
  ['unresolved', 'unresolved_land', 'Unresolved', '#b8b8b8'],
  ['private', 'private_land', 'Private', '#d9d9d9'],
]

// Ownership shares (%) with something to draw.
export const landShares = (land) => {
  if (!land) return []
  return LAND_CLASSES
    .map(([key, raw, label, color]) => ({ key, label, color, pct: Number(pick(land, key, raw, camel(raw))) || 0 }))
    .filter((share) => share.pct > 0)
}

// Wilderness (%), part of the agencies' land rather than a share of its own; 0 when none.
export const wildernessShare = (land) => Number(pick(land, 'wilderness', 'wild_land', 'wildLand')) || 0

// The terrain files' slope bins are degrees, though labelled "+/- 0-5%": grouped for display.
const SLOPE_GROUPS = [
  ['Flat', '0–5°', ['+/- 0-5%', '0-5']],
  ['Rolling', '5–15°', ['+/- 5-10%', '+/- 10-15%', '5-10', '10-15']],
  ['Steep', '15–35°', ['+/- 15-25%', '+/- 25-35%', '15-25', '25-35']],
  ['Very steep', '35°+', ['+/- 35-45%', '+ 45%', '35-45', '45+']],
]

// API-3847's slope bins, [{ minDegrees, maxDegrees, percent }] (the top one open-ended), or the
// terrain files' { '+/- 0-5%': pct, ... }.
const SLOPE_DEGREES = [['Flat', '0–5°', 0, 5], ['Rolling', '5–15°', 5, 15], ['Steep', '15–35°', 15, 35], ['Very steep', '35°+', 35, Infinity]]

const slopeGroups = (bins) => {
  if (!bins) return null
  const groups = Array.isArray(bins)
    ? SLOPE_DEGREES.map(([label, range, lo, hi]) => ({
      label, range,
      pct: bins.filter((b) => Number(b.minDegrees) >= lo && Number(b.minDegrees) < hi).reduce((sum, b) => sum + (Number(b.percent) || 0), 0),
    }))
    : SLOPE_GROUPS.map(([label, range, keys]) => ({
      label, range, pct: keys.reduce((sum, key) => sum + (Number(bins[key]) || 0), 0),
    }))
  return groups.some((g) => g.pct > 0) ? groups : null
}

const landTerrain = (side) => {
  if (!side) return null
  const elevation = pick(side, 'elevation') || {}
  const slope = pick(side, 'slope') || {}
  const out = {
    elevationMin: pick(side, 'elevationMin') ?? pick(elevation, 'min'),
    elevationMedian: pick(side, 'elevationMedian') ?? pick(elevation, 'median', '50%'),
    elevationMax: pick(side, 'elevationMax') ?? pick(elevation, 'max'),
    slopeMean: pick(side, 'slopeMean') ?? pick(slope, 'mean'),
    slope: slopeGroups(pick(side, 'slopeBins', 'slope_bins', 'slopeDistribution')),
  }
  return out.elevationMedian !== undefined || out.slope ? out : null
}

// Terrain on public and on private land; null when the unit has neither.
export const unitTerrain = (unitDetails) => {
  const terrain = unitDetails?.terrain
  const out = { public: landTerrain(terrain?.public), private: landTerrain(terrain?.private) }
  return out.public || out.private ? out : null
}

const SERIES = {
  avg_max_tmp: 'avgHigh', avg_min_tmp: 'avgLow', max_max_tmp: 'recordHigh', min_min_tmp: 'recordLow',
  avg_precip: 'precip', prob_precip: 'precipChance', prob_snow_ground: 'snowChance', avg_snow_depth: 'snowDepth',
}

const bandLow = (band) => parseInt(band, 10)

// Monthly normals per 1,000 ft elevation band, lowest band first:
// [{ band: '4000-5000', months: [{ month: 1, avgHigh, avgLow, recordHigh, recordLow, precip, precipChance, snowChance, snowDepth }] }]
// From the API's bands list, or from geo_weather's weather_data ({ band: { temp|precip|snow: [{ y_label, x_values, y_values }] } }).
export const climateBands = (climate) => {
  if (!climate) return []
  const raw = pick(climate, 'bands', 'weatherData', 'weather_data')
  if (!raw) return []
  const bands = Array.isArray(raw)
    ? raw.map((b) => ({ band: b.band || `${b.minElevation}-${b.maxElevation}`, months: b.months || [] }))
    : Object.entries(raw).map(([band, groups]) => {
      const months = Array.from({ length: 12 }, (_, i) => ({ month: i + 1 }))
      Object.values(groups || {}).forEach((series) => (Array.isArray(series) ? series : []).forEach((s) => {
        const name = SERIES[s.y_label || s.yLabel]
        if (!name) return
        const xs = s.x_values || s.xValues || []
        const ys = s.y_values || s.yValues || []
        xs.forEach((m, i) => { if (months[m - 1]) months[m - 1][name] = ys[i] })
      }))
      return { band, months }
    })
  return bands
    .filter((b) => b.band !== 'all' && b.months.some((m) => m.avgHigh !== undefined))
    .sort((a, b) => bandLow(a.band) - bandLow(b.band))
}

export const climateSources = (climate) => pick(climate, 'sources') || []

const WIND_DIRECTIONS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']

// Monthly wind and sky as [{ month, directions: { N: pct, ... }, avgWindSpeed, maxWindSpeed, sunshine }], from
// API-3847's monthly list or geo_weather's weather_monthly ({ '1': { N, ..., avg_wind_speed } }). maxWindSpeed is
// the average daily peak; sunshine is the % of clear-sky sun (cloud cover is 100 less it).
export const climateWind = (climate) => {
  const raw = pick(climate, 'monthly', 'weatherMonthly', 'weather_monthly')
  if (!raw) return []
  const rows = Array.isArray(raw) ? raw : Object.entries(raw).map(([month, v]) => ({ month: +month, ...v }))
  return rows.map((r) => {
    const dirs = r.windDirection || r
    return {
      month: +r.month,
      directions: Object.fromEntries(WIND_DIRECTIONS.map((d) => [d, Number(dirs[d] ?? dirs[d.toLowerCase()]) || 0])),
      avgWindSpeed: Number(pick(r, 'avgWindSpeed', 'avg_wind_speed')),
      maxWindSpeed: Number(pick(r, 'maxWindSpeed', 'max_wind_speed')),
      sunshine: Number(pick(r, 'sunshine', 'avg_sunshine')),
    }
  })
}

const mean = (values) => {
  const nums = values.filter(Number.isFinite)
  return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : null
}

const inMonths = (rows, months) => rows.filter((r) => !months?.size || months.has(r.month))

// The prevailing wind over these months (all of them when none):
// "Mostly from the SW (24% of the time), averaging 6 mph, daily peaks near 15 mph".
export const prevailingWind = (wind, months) => {
  const rows = inMonths(wind, months)
  if (!rows.length) return null
  const [dir, pct] = WIND_DIRECTIONS
    .map((d) => [d, rows.reduce((s, r) => s + r.directions[d], 0) / rows.length])
    .sort((a, b) => b[1] - a[1])[0]
  if (!pct) return null
  const speed = mean(rows.map((r) => r.avgWindSpeed))
  const peak = mean(rows.map((r) => r.maxWindSpeed))
  return `Mostly from the ${dir} (${Math.round(pct)}% of the time)`
    + (speed !== null ? `, averaging ${Math.round(speed)} mph` : '')
    + (peak !== null && (speed === null || Math.round(peak) > Math.round(speed)) ? `, daily peaks near ${Math.round(peak)} mph` : '')
}

// Sun over these months: "Mostly sunny: about 77% of clear-sky sun"; null without data.
export const skyText = (wind, months) => {
  const sun = mean(inMonths(wind, months).map((r) => r.sunshine))
  if (sun === null) return null
  const sky = sun >= 70 ? 'Mostly sunny' : sun >= 50 ? 'Partly sunny' : 'Mostly cloudy'
  return `${sky}: about ${Math.round(sun)}% of clear-sky sun`
}

// The band in the middle of the unit's range: the default the chart shows.
export const middleBand = (bands) => (bands.length ? bands[Math.floor((bands.length - 1) / 2)].band : null)

export const bandLabel = (band) => {
  const [lo, hi] = band.split('-').map((n) => parseInt(n, 10))
  return Number.isFinite(hi) ? `${lo.toLocaleString()}–${hi.toLocaleString()} ft` : band
}

// Months (1–12) any of these seasons is open in.
export const seasonMonths = (seasons) => {
  const months = new Set()
  for (const season of seasons || []) {
    if (!season?.startDate || !season?.endDate) continue
    const start = new Date(season.startDate)
    const end = new Date(season.endDate)
    if (isNaN(start) || isNaN(end)) continue
    const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1))
    for (let guard = 0; cursor <= end && guard < 24; guard++) {
      months.add(cursor.getUTCMonth() + 1)
      cursor.setUTCMonth(cursor.getUTCMonth() + 1)
    }
  }
  return months
}

const range = (values) => {
  const nums = values.filter((v) => Number.isFinite(v))
  return nums.length ? [Math.min(...nums), Math.max(...nums)] : null
}

const most = (values) => {
  const nums = values.filter(Number.isFinite)
  return nums.length ? Math.max(...nums) : null
}

// Normals over these months in one band, or null: { highs: [lo, hi], lows: [lo, hi], recordLow, recordHigh,
// precipChance (the wettest month's chance of a wet day), snowChance, snowDepth (on days with snow) }.
export const normalsForMonths = (bands, band, months) => {
  const rows = (bands.find((b) => b.band === band)?.months || []).filter((m) => months.has(m.month))
  if (!rows.length) return null
  const highs = range(rows.map((m) => m.avgHigh))
  const lows = range(rows.map((m) => m.avgLow))
  if (!highs && !lows) return null
  const recordLows = rows.map((m) => m.recordLow).filter(Number.isFinite)
  return {
    highs, lows,
    recordLow: recordLows.length ? Math.min(...recordLows) : null,
    recordHigh: most(rows.map((m) => m.recordHigh)),
    precipChance: most(rows.map((m) => m.precipChance)),
    snowChance: most(rows.map((m) => m.snowChance)),
    snowDepth: most(rows.map((m) => m.snowDepth)),
  }
}

export const normalsForDates = (bands, band, startDate, endDate) =>
  normalsForMonths(bands, band, seasonMonths([{ startDate, endDate }]))

// How much colder the top band is than the bottom one over these months (average highs), when it's at
// least 3°F: "About 12°F colder at 9,000–10,000 ft than at 5,000–6,000 ft".
export const bandDifference = (bands, months) => {
  if (bands.length < 2) return null
  const highs = (b) => mean(b.months.filter((m) => !months?.size || months.has(m.month)).map((m) => m.avgHigh))
  const low = bands[0], top = bands[bands.length - 1]
  const lowHigh = highs(low), topHigh = highs(top)
  if (lowHigh === null || topHigh === null || Math.round(lowHigh - topHigh) < 3) return null
  return `About ${Math.round(lowHigh - topHigh)}°F colder at ${bandLabel(top.band)} than at ${bandLabel(low.band)}`
}

const span = ([lo, hi]) => (Math.round(lo) === Math.round(hi) ? `${Math.round(lo)}` : `${Math.round(lo)}–${Math.round(hi)}`)

const has = (v) => v !== null && v !== undefined && Number.isFinite(v)
const degrees = (v) => `${Math.round(v) < 0 ? '−' : ''}${Math.abs(Math.round(v))}°F`

// "highs 58–70°F, lows 25–35°F (records −11°F to 89°F), 28% chance of rain or snow on a given day,
// 10% chance of snow on the ground (about 4 in when there is)"
export const normalsText = (normals) => {
  if (!normals) return ''
  const parts = []
  if (normals.highs) parts.push(`highs ${span(normals.highs)}°F`)
  if (normals.lows) parts.push(`lows ${span(normals.lows)}°F`)
  if (parts.length && has(normals.recordLow) && has(normals.recordHigh)) {
    parts[parts.length - 1] += ` (records ${degrees(normals.recordLow)} to ${degrees(normals.recordHigh)})`
  }
  if (has(normals.precipChance)) parts.push(`${Math.round(normals.precipChance)}% chance of rain or snow on a given day`)
  if (has(normals.snowChance)) {
    const depth = has(normals.snowDepth) && Math.round(normals.snowDepth) > 0 && Math.round(normals.snowChance) > 0
      ? ` (about ${Math.round(normals.snowDepth)} in when there is)` : ''
    parts.push(`${Math.round(normals.snowChance)}% chance of snow on the ground${depth}`)
  }
  return parts.join(', ')
}
